import { ActionReducer } from '@ngrx/store';
import { RouterState } from '@ngrx/router-store';
import { createSelector } from 'reselect';
import * as fromRouter from '@ngrx/router-store';

import { Observable } from 'rxjs/Observable';

import { User } from '@api/models/user';

declare var global: any;

/**
 * The compose function is one of our most handy tools. In basic terms, you give
 * it any number of functions and it returns a function. This new function
 * takes a value and chains it through every composed function, returning
 * the output.
 *
 * More: https://drboolean.gitbooks.io/mostly-adequate-guide/content/ch5.html
 */
import { compose } from '@ngrx/core/compose';

/**
 * combineReducers is another useful metareducer that takes a map of reducer
 * functions and creates a new reducer that gathers the values
 * of each reducer and stores them using the reducer's key. Think of it
 * almost like a database, where every reducer is a table in the db.
 *
 * More: https://egghead.io/lessons/javascript-redux-implementing-combinereducers-from-scratch
 */
import { combineReducers } from '@ngrx/store';

/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */
import * as fromUser from './reducers/user.reducer';
import * as fromUI from './reducers/ui.reducer';
import * as fromUIHeader from './reducers/ui-header.reducer';

/**
 * The store keeps the different states of an application
 */
export interface AppState {
  user: fromUser.UserState;
  ui: fromUI.UIState;
  uiHeader: fromUIHeader.UIHeaderState
  router: RouterState;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
const reducers = {
  user: fromUser.reducer,
  ui: fromUI.reducer,
  uiHeader: fromUIHeader.reducer,
  router: fromRouter.routerReducer,
};

/**
 * Create shareable function elps to add reducers inside Feature Modules
 */
global.createReducer = function (asyncReducers = {}): ActionReducer<any> {
  let reducerTemp;
  if ((typeof window['coreStore'] === "undefined") || (window['coreStore'] === null)) {
    reducerTemp = reducers;
    window['coreStore'] = reducers;
  }
  else reducerTemp = window['coreStore'];

  return combineReducers(Object.assign(reducerTemp, asyncReducers));
}

// Here is the set of reducers in main application
const commonReducer: ActionReducer<AppState> = global.createReducer();

/**
 * Reducer function. The @ngrx store calls this one in the moment of creating
 * the new state of entire application
 */
export function reducer(state: any, action: any) {
  return commonReducer(state, action);
}

/**
 * A selector function is a map function factory. We pass it parameters and it
 * returns a function that maps from the larger state tree into a smaller
 * piece of state. This selector simply selects the `some` state.
 *
 * Selectors are used with the `select` operator.
 *
 * ```ts
 * class MyComponent {
 * 	constructor(state$: Observable<State>) {
 * 	  this.someState$ = state$.select(getSomeState);
 * 	}
 * }
 * ```
 */

/**
 * Every reducer module exports selector functions, however child reducers
 * have no knowledge of the overall state tree. To make them useable, we
 * need to make new selectors that wrap them.
 *
 * The createSelector function from the reselect library creates
 * very efficient selectors that are memoized and only recompute when arguments change.
 * The created selectors can also be composed together to select different
 * pieces of state.
 */

/**
 * Just like with the projects selectors, we also have to compose the search
 * reducer's and collection reducer's selectors.
 */

//------------------------------------------
// RouterState
//------------------------------------------
export const getRouter = (state: AppState) => state.router; 

//------------------------------------------
// UserState
//------------------------------------------
export const getUserState = (state: AppState) => state.user;
export const getUser = createSelector(getUserState, fromUser.getUser);
export const getIsAuthenticated = createSelector(getUserState, fromUser.getIsAuthenticated);

//------------------------------------------
// General UIState
//------------------------------------------
export const getUItate = (state: AppState) => state.ui;
export const getMenus = createSelector(getUItate, fromUI.getMenus);
export const getWidgets = createSelector(getUItate, fromUI.getWidgets);
export const getDashboards = createSelector(getUItate, fromUI.getDashboards);
export const getCollapsedSidenav = createSelector(getUItate, fromUI.getCollapsedSidenav);
export const getTheme = createSelector(getUItate, fromUI.getTheme);
export const getProgressIndicator = createSelector(getUItate, fromUI.getProgressIndicator);

//------------------------------------------
// UIHeaderState
//------------------------------------------
export const getUIHeaderState = (state: AppState) => state.uiHeader;
export const getHeaderBackground = createSelector(getUIHeaderState, fromUIHeader.getHeaderBackground);
export const getHeaderBackgroundColor = createSelector(getUIHeaderState, fromUIHeader.getHeaderBackgroundColor);
export const getMastheadHubname = createSelector(getUIHeaderState, fromUIHeader.getMastheadHubname);
export const getMastheadLogo = createSelector(getUIHeaderState, fromUIHeader.getMastheadLogo);
export const getJumboTitle = createSelector(getUIHeaderState, fromUIHeader.getJumboTitle);
export const getJumboCaption = createSelector(getUIHeaderState, fromUIHeader.getJumboCaption);
export const getJumboVariant = createSelector(getUIHeaderState, fromUIHeader.getJumboVariant);
export const showJumbo = createSelector(getUIHeaderState, fromUIHeader.showJumbo);
export const getTopnavItems = createSelector(getUIHeaderState, fromUIHeader.getTopnavItems);
export const getContextnavItems = createSelector(getUIHeaderState, fromUIHeader.getContextnavItems);


// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./~/angular-router-loader/src!./src/app/celine/store/app-store.ts